import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  clearCart,
  getCart,
  removeCartItem,
  updateCartItem,
} from "../service/CartService";
import { placeOrder } from "../service/OrderService";
import { Alert } from "../util/Alert";
import { Loading } from "../util/Loading";
import { Cart } from "../model/types";
import { extractErrorMessage } from "../service/api";

export const CartPage = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState<Cart | null>(null);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadCart = async () => {
    setLoading(true);
    try {
      setCart(await getCart());
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCart();
  }, []);

  const handleUpdate = async (cartItemId: number, quantity: number) => {
    try {
      const nextCart = await updateCartItem(cartItemId, { quantity });
      setCart(nextCart);
      setMessage("Cart updated");
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleRemove = async (cartItemId: number) => {
    try {
      const nextCart = await removeCartItem(cartItemId);
      setCart(nextCart);
      setMessage("Item removed");
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleClearCart = async () => {
    try {
      const nextCart = await clearCart();
      setCart(nextCart);
      setMessage("Cart cleared");
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handlePlaceOrder = async () => {
    if (!deliveryAddress.trim()) {
      setError("Delivery address is required");
      return;
    }
    try {
      const order = await placeOrder({ deliveryAddress });
      setDeliveryAddress("");
      await loadCart();
      navigate("/orders", {
        state: {
          message: `Order ID:${order.orderId} placed successfully.`,
        },
      });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  if (loading) {
    return <Loading label="Loading cart..." />;
  }

  return (
    <section className="space-y-6">
      <div className="rounded-3xl bg-white p-6 shadow-sm">
        <p className="text-xs uppercase tracking-[0.24em] text-emerald-700">
          Cart
        </p>
        <h2 className="mt-2 text-3xl font-semibold text-slate-900">
          Review and place your order
        </h2>
      </div>

      {message ? (
        <Alert type="success" message={message} onClose={() => setMessage("")} />
      ) : null}
      {error ? (
        <Alert type="failed" message={error} onClose={() => setError("")} />
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1.3fr_0.8fr]">
        <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h3 className="text-lg font-semibold text-slate-900">Cart items</h3>
            <button
              type="button"
              onClick={handleClearCart}
              disabled={!cart?.items.length}
              className="rounded-2xl border border-rose-200 px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:text-rose-300"
            >
              Clear cart
            </button>
          </div>

          <div className="space-y-4">
            {cart?.items.length ? (
              cart.items.map((item) => (
                <div
                  key={item.cartItemId}
                  className="flex flex-col gap-4 rounded-2xl border border-stone-200 p-4 md:flex-row md:items-center md:justify-between"
                >
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      {item.foodName}
                    </h3>
                    <p className="text-sm text-stone-600">
                      Rs. {item.unitPrice.toFixed(2)} each
                    </p>
                    <p className="text-xs text-stone-500">
                      {item.stockQuantity} units currently in stock
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min={1}
                      max={Math.max(1, item.stockQuantity)}
                      value={item.quantity}
                      onChange={(event) =>
                        handleUpdate(item.cartItemId, Number(event.target.value))
                      }
                      className="w-20 rounded-2xl border border-stone-300 px-3 py-2"
                    />
                    <div className="min-w-28 text-right text-sm font-semibold text-slate-900">
                      Rs. {item.lineTotal.toFixed(2)}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemove(item.cartItemId)}
                      className="rounded-2xl border border-rose-200 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-stone-600">Your cart is empty.</p>
            )}
          </div>
        </div>

        <aside className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Checkout</h3>
          <p className="mt-2 text-sm text-stone-600">
            Enter your phone number and delivery address before placing the order.
          </p>
          <div className="mt-5 space-y-4">
            <textarea
              rows={5}
              value={deliveryAddress}
              onChange={(event) => setDeliveryAddress(event.target.value)}
              className="w-full rounded-2xl border border-stone-300 px-4 py-3"
              placeholder={"Phone number\n\nDelivery address"}
             

            />
            <div className="flex items-center justify-between text-sm">
              <span className="text-stone-600">Total</span>
              <span className="text-lg font-semibold text-slate-900">
                Rs. {cart?.totalAmount.toFixed(2) ?? "0.00"}
              </span>
            </div>
            <button
              type="button"
              onClick={handlePlaceOrder}
              disabled={!cart?.items.length}
              className="w-full rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
            >
              Place order
            </button>
          </div>
        </aside>
      </div>
    </section>
  );
};
